// Increment 39: reacceptance gate. When a signed-in member owes acceptance of an
// updated ToS/Privacy version, show a blocking modal that must be accepted before
// they can continue taking actions. Mounted globally in the app Shell.
import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Modal } from "./beui/Modal";
import { PolicyTabs } from "./beui/PolicyControls";
import { PolicyBody } from "./legal/PolicyBody";
import "../pages/LegalHub.css";

type LegalDocument = { kind: string; version: number; title: string; body: string; publishedAt: string };
type LegalStatus = {
  terms: { current: number; needsAcceptance: boolean };
  privacy: { current: number; needsAcceptance: boolean };
  needsAcceptance: boolean;
};

export default function LegalGate() {
  const { user } = useAuth();
  const [status, setStatus] = useState<LegalStatus | null>(null);
  const [documents, setDocuments] = useState<Record<string, LegalDocument>>({});
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [statusError, setStatusError] = useState(false);
  const [selectedKind, setSelectedKind] = useState<"terms" | "privacy">("terms");
  const requestScope = useRef(0);

  async function check() {
    const scope = ++requestScope.current;
    setStatusError(false);
    if (!user || user.userType === "admin") { setStatus(null); return; }
    let nextStatus: LegalStatus;
    try {
      nextStatus = await api.legalStatus();
    } catch {
      if (scope === requestScope.current) { setStatus(null); setStatusError(true); }
      return;
    }
    if (scope !== requestScope.current) return;
    setStatus(nextStatus);
    if (!nextStatus?.needsAcceptance) { setDocuments({}); setDocumentsLoading(false); return; }
    const kinds = [
      ...(nextStatus.terms?.needsAcceptance ? ["terms" as const] : []),
      ...(nextStatus.privacy?.needsAcceptance ? ["privacy" as const] : []),
    ];
    setDocuments({});
    setDocumentsLoading(true);
    try {
      const entries: Array<readonly ["terms" | "privacy", LegalDocument | null]> = await Promise.all(kinds.map(async (kind) => [kind, await api.legalDoc(kind)] as const));
      if (scope !== requestScope.current) return;
      setDocuments(Object.fromEntries(entries.filter((entry): entry is readonly ["terms" | "privacy", LegalDocument] => !!entry[1])));
    } catch { if (scope === requestScope.current) setDocuments({}); }
    finally { if (scope === requestScope.current) setDocumentsLoading(false); }
  }
  useEffect(() => { void check(); return () => { ++requestScope.current; }; /* eslint-disable-next-line */ }, [user?.id]);

  if (!user || user.userType === "admin") return null;
  if (statusError) return <div role="alert" className="container-x py-3 text-sm text-brand-red">
    Could not check your policy acceptance. Protected changes are temporarily unavailable.
    <button type="button" className="ml-2 underline font-semibold" onClick={() => void check()}>Retry policy check</button>
  </div>;
  if (!status?.needsAcceptance) return null;

  const pending: string[] = [];
  if (status.terms?.needsAcceptance) pending.push("Terms of Service");
  if (status.privacy?.needsAcceptance) pending.push("Privacy Policy");

  async function accept() {
    setBusy(true); setErr(null);
    try { await api.acceptLegal(); await check(); }
    catch (failure: unknown) { setErr(failure instanceof Error ? failure.message : "Could not record acceptance."); }
    finally { setBusy(false); }
  }

  const pendingKinds = [
    ...(status.terms?.needsAcceptance ? ["terms" as const] : []),
    ...(status.privacy?.needsAcceptance ? ["privacy" as const] : []),
  ];
  const documentsReady = !documentsLoading && pendingKinds.every((kind) => documents[kind]);
  const activeKind = pendingKinds.includes(selectedKind) ? selectedKind : pendingKinds[0];
  const activeDocument = documents[activeKind];

  return (
    <Modal label="Review updated policies" onClose={() => undefined} dismissible={false}>
      <div className="legal-gate flex min-h-0 flex-col" data-testid="legal-gate">
        <header className="shrink-0 px-5 pt-6 pb-4 sm:px-7">
          <div className="flex items-center gap-3"><ShieldCheck className="h-6 w-6 shrink-0 text-navy" aria-hidden="true" /><h2 className="text-xl font-extrabold text-navy">We've updated our {pending.length > 1 ? "policies" : "policy"}</h2></div>
          <p className="mt-2 text-sm text-mut">Please review the updated {pending.join(" and ")} before accepting.</p>
        </header>
        <div className="shrink-0 px-5 pb-4 sm:px-7"><PolicyTabs id="legal-review" label="Updated policy documents"
          options={pendingKinds.map(kind => ({ value: kind, label: `${kind === "terms" ? "Terms of Service" : "Privacy Policy"} · v${documents[kind]?.version ?? status[kind].current}` }))}
          value={activeKind} disabled={busy} onChange={kind => { if (kind === "terms" || kind === "privacy") setSelectedKind(kind); return true; }} /></div>
        <div id="legal-review-panel" role="tabpanel" aria-labelledby={`legal-review-${activeKind}`} tabIndex={0}
          className="overflow-y-auto min-h-0 max-h-[50dvh] px-5 pb-6 sm:px-7" aria-label="Updated policy text">
          {documentsLoading ? <p role="status" className="text-sm text-mut">Loading the updated policy text…</p>
            : !documentsReady ? <div role="alert"><p className="text-sm text-mut">The document text is unavailable. Please try again.</p><button type="button" onClick={() => void check()} className="btn-outline mt-3">Retry documents</button></div>
              : activeDocument && <PolicyBody body={activeDocument.body} />}
        </div>
        <footer className="shrink-0 border-t border-line bg-navy-50/30 px-5 py-4 sm:px-7">
          {err && <p role="alert" className="mb-3 text-sm text-brand-red">{err}</p>}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <a href={`/legal/${activeKind}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-navy underline">Open full {activeKind === "terms" ? "Terms of Service" : "Privacy Policy"}</a>
            <button type="button" onClick={accept} disabled={busy || !documentsReady} className="btn-red min-h-11 disabled:opacity-60" data-testid="legal-accept">
              {busy ? <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" /> : <ShieldCheck className="h-4 w-4" aria-hidden="true" />}{busy ? "Saving…" : "I agree and accept"}
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
}
